import { generateTitle } from 'utils/charts/common';
import {
    CONCENTRATED_EO_TRADES,
    CONCENTRATED_EO_TRADES_SCORE,
    NON_OUTLIER_RM_ICON_URL,
    RED_DIAMOND_URL,
    NON_OUTLIER_RM,
    OUTLIER_RM
} from 'utils/charts/constants';
import GreyCircle from 'assets/images/grey-circle.svg';
import RedCircle from 'assets/images/red-circle.svg';
import GreyRMIcon from 'assets/images/grey-rm-icon.svg';

const nonOutlierRM = {
    image: {
        type: 'image',
        style: {
            image: GreyCircle,
            width: 12,
            height: 12,
            opacity: 1
        },
        cursor: 'default',
        bottom: 10,
        left: '32%'
    },
    text: {
        type: 'text',
        style: {
            text: NON_OUTLIER_RM,
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '35.2%'
    }
};

const outlierRM = {
    image: {
        type: 'image',
        style: {
            image: RedCircle,
            width: 12,
            height: 12
        },
        cursor: 'default',
        bottom: 10,
        left: '57%'
    },
    text: {
        type: 'text',
        style: {
            text: OUTLIER_RM,
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        bottom: 10,
        left: '60.2%'
    }
};

const rmSymbolAndTextGraphic = {
    image: {
        type: 'image',
        style: {
            image: GreyRMIcon,
            width: 12,
            height: 12
        },
        cursor: 'default',
        top: 5,
        right: 45
    },
    text: {
        type: 'text',
        style: {
            text: 'RM',
            font: '10px "Outfit" 400',
            fill: '#929292'
        },
        cursor: 'default',
        top: 5,
        right: 20
    }
};

const partOneTooltip = (outliers) => {
    return {
        formatter: function (params) {
            const { name } = params;
            const hoveredOutlier = outliers
                ? outliers.find((o) => o.rmEmployeeId === name)
                : [];
            const { rmRegion, rmGroup, rmEmployeeId, maxClient, rank, score } =
                { ...hoveredOutlier };
            return (
                '<span style="line-height: 30px;">CTO Code</span>' +
                '<span style="font-weight: 400; margin-left: 8px; margin-bottom: 5px; color: #929292;">' +
                rmEmployeeId +
                '</span>' +
                '<br><span style="line-height: 30px;">Team</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rmGroup +
                '</span>' +
                '<br><span style="line-height: 30px;">Market</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rmRegion +
                '</span>' +
                '<br><span style="line-height: 30px;">Max Client : Product</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                maxClient +
                ' : 1' +
                '</span>' +
                '<br><span style="line-height: 30px;">S4 Rank</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                rank +
                '</span>' +
                '<br><span style="line-height: 30px;">S4 Risk Score</span>' +
                '<span style="font-weight: 400; margin-left: 8px; color: #929292;">' +
                score +
                '</span>'
            );
        },
        textStyle: {
            color: '#000000',
            fontWeight: 700,
            fontSize: '10px'
        },
        extraCssText:
            'box-shadow: 0px 0px 20px rgba(0, 0, 0, 0.08); ' +
            'border: 1px solid white; ' +
            'border-radius: 16px; ' +
            'padding: 16px;'
    };
};

const calculateSymbolForOutliers = (value, outlierMin) => {
    if (value < outlierMin) return NON_OUTLIER_RM_ICON_URL;
    else return RED_DIAMOND_URL;
};

export const scenarioFourOutlierChartOptions = (data, selectedRm) => {
    const { max, min, outlierMin, outliers } = data;
    const titleText = selectedRm
        ? `${CONCENTRATED_EO_TRADES} - ${selectedRm}`
        : CONCENTRATED_EO_TRADES;

    return {
        title: generateTitle(titleText),
        tooltip: partOneTooltip(outliers),
        xAxis: {
            type: 'category',
            axisTick: { show: false },
            axisLine: { show: false },
            axisLabel: { show: false }
        },
        yAxis: [
            {
                type: 'value',
                name: CONCENTRATED_EO_TRADES_SCORE,
                nameLocation: 'center',
                nameGap: 65,
                nameTextStyle: {
                    padding: [20, 0, 0, 0],
                    color: '#8DA8B3',
                    fontWeight: 'bold'
                },
                splitArea: {
                    show: true,
                    areaStyle: {
                        color: '#FFFFFF'
                    }
                },
                splitLine: {
                    lineStyle: {
                        color: ['#FFE6BE', '#FFD4DB'],
                        width: 1.5
                    }
                },
                interval: max,
                min: data ? min : 0,
                max: data ? max : 100
            },
            {
                type: 'value',
                name: 'Min',
                nameTextStyle: {
                    align: 'left',
                    verticalAlign: 'top',
                    padding: [298, 0, 0, 8]
                }
            },
            {
                type: 'value',
                name: 'Max',
                nameTextStyle: {
                    align: 'left',
                    verticalAlign: 'top',
                    padding: [10, 0, 0, 8]
                }
            }
        ],
        series: [
            {
                type: 'scatter',
                symbolSize: 12,
                data: data
                    ? outliers.map((outlier, index) => {
                          return {
                              value: [index, outlier.score],
                              name: outlier.rmEmployeeId,
                              symbol: calculateSymbolForOutliers(
                                  outlier.score,
                                  outlierMin
                              ),
                              symbolSize: 12,
                              emphasis: {
                                  focus: 'self',
                                  label: {
                                      show: true,
                                      position: 'top',
                                      color: '#244B60',
                                      fontSize: '12px',
                                      fontWeight: 700,
                                      formatter: function (params) {
                                          return params.name;
                                      }
                                  }
                              },
                              blur: {
                                  label: {
                                      show: false
                                  }
                              }
                          };
                      })
                    : []
            }
        ],
        grid: {
            width: '320px',
            left: '80px'
        },
        graphic: [
            nonOutlierRM.image,
            nonOutlierRM.text,
            outlierRM.image,
            outlierRM.text,
            rmSymbolAndTextGraphic.image,
            rmSymbolAndTextGraphic.text
        ]
    };
};
